import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import type { ReactNode } from 'react';

import type { ContactKind, ContactLabel, ContactType, ContactWebsiteKind } from '../../api/business/types';
import {
  emptyAddress,
  emptyEmail,
  emptyPerson,
  emptyPhone,
  emptyWebsite,
  markBilling,
  markPrimary,
  removePrimaryRow,
  type ContactDraft,
} from '../../components/contactDraft';
import { useT } from '../../i18n/LanguageContext';

const KINDS: Array<ContactKind> = ['ORGANISATION', 'PERSON'];

const TYPES: Array<ContactType> = ['CUSTOMER', 'SUPPLIER', 'PARTNER', 'OTHER'];

const WEBSITE_KINDS: Array<ContactWebsiteKind> = ['WEB', 'FACEBOOK', 'INSTAGRAM', 'LINKEDIN', 'OTHER'];

const Section = ({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 2.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      {action}
    </Stack>
    <Stack spacing={1.5}>{children}</Stack>
  </Paper>
);

const AddButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button size="small" startIcon={<AddIcon />} onClick={onClick}>
    {label}
  </Button>
);

type ContactFormProps = {
  draft: ContactDraft;
  onChange: (draft: ContactDraft) => void;
  labels: Array<ContactLabel>;
};

/**
 * A contact's whole card, editable (R0039): the details, the labels, and every list — e-mails and phones with one
 * primary, addresses with one billing, people and links — and the description. Saved whole by the page.
 */
export const ContactForm = ({ draft, onChange, labels }: ContactFormProps) => {
  const t = useT();
  const set = <TKey extends keyof ContactDraft>(key: TKey, value: ContactDraft[TKey]): void =>
    onChange({ ...draft, [key]: value });
  const setRow = <TKey extends 'emails' | 'phones' | 'addresses' | 'persons' | 'websites'>(
    key: TKey,
    index: number,
    changes: Partial<ContactDraft[TKey][number]>,
  ): void =>
    set(
      key,
      draft[key].map((row, at) => (at === index ? { ...row, ...changes } : row)) as ContactDraft[TKey],
    );
  const removeRow = <TKey extends 'addresses' | 'persons' | 'websites'>(key: TKey, index: number): void =>
    set(key, draft[key].filter((_row, at) => at !== index) as ContactDraft[TKey]);

  const removeButton = (onClick: () => void) => (
    <Tooltip title={t('contact.remove')}>
      <IconButton size="small" onClick={onClick}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
  const primaryButton = (isPrimary: boolean, onClick: () => void) => (
    <Tooltip title={isPrimary ? t('contact.primary') : t('contact.makePrimary')}>
      <IconButton size="small" color={isPrimary ? 'warning' : 'default'} onClick={onClick}>
        {isPrimary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );

  return (
    <Stack spacing={2}>
      <Section title={t('contact.section.identity')}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              autoFocus
              label={t('contact.name')}
              value={draft.name}
              onChange={(event) => set('name', event.target.value)}
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              label={t('contacts.type')}
              value={draft.type}
              onChange={(event) => set('type', event.target.value as ContactType)}
            >
              {TYPES.map((one) => (
                <MenuItem key={one} value={one}>
                  {t(`contactType.${one}` as const)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              label={t('contacts.kind')}
              value={draft.kind}
              onChange={(event) => set('kind', event.target.value as ContactKind)}
            >
              {KINDS.map((one) => (
                <MenuItem key={one} value={one}>
                  {t(`contactKind.${one}` as const)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('contact.legalName')}
              value={draft.legalName}
              onChange={(event) => set('legalName', event.target.value)}
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
          <Grid size={{ xs: 4, md: 2 }}>
            <TextField
              fullWidth
              label={t('contact.ico')}
              value={draft.ico}
              onChange={(event) => set('ico', event.target.value)}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid size={{ xs: 4, md: 2 }}>
            <TextField
              fullWidth
              label={t('contact.dic')}
              value={draft.dic}
              onChange={(event) => set('dic', event.target.value)}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid size={{ xs: 4, md: 2 }}>
            <TextField
              fullWidth
              label={t('contact.icDph')}
              value={draft.icDph}
              onChange={(event) => set('icDph', event.target.value)}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Autocomplete
              multiple
              options={labels}
              getOptionLabel={(label) => label.name}
              value={labels.filter((label) => draft.labelIds.includes(label.id))}
              onChange={(_event, chosen) =>
                set(
                  'labelIds',
                  chosen.map((label) => label.id),
                )
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={t('contact.noLabels')}
              renderTags={(value, getTagProps) =>
                value.map((label, index) => {
                  const { key, ...props } = getTagProps({ index });

                  return <Chip key={key} size="small" label={label.name} {...props} />;
                })
              }
              renderInput={(params) => <TextField {...params} label={t('contact.section.labels')} />}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.doNotContact}
                  onChange={(event) => set('doNotContact', event.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('contacts.doNotContact')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('contact.doNotContactHint')}
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </Section>

      <Section
        title={t('contact.section.emails')}
        action={
          <AddButton
            label={t('contact.addEmail')}
            onClick={() => set('emails', [...draft.emails, emptyEmail(draft.emails.length === 0)])}
          />
        }
      >
        {draft.emails.map((row, index) => (
          <Stack
            key={index}
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            {primaryButton(row.isPrimary, () => set('emails', markPrimary(draft.emails, index)))}
            <TextField
              size="small"
              type="email"
              label={t('contacts.email')}
              value={row.email}
              onChange={(event) => setRow('emails', index, { email: event.target.value })}
              sx={{ flex: 2 }}
            />
            <TextField
              size="small"
              label={t('contact.rowLabel')}
              placeholder={t('contact.rowLabelHint')}
              value={row.label}
              onChange={(event) => setRow('emails', index, { label: event.target.value })}
              inputProps={{ maxLength: 60 }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label={t('contact.note')}
              value={row.note}
              onChange={(event) => setRow('emails', index, { note: event.target.value })}
              inputProps={{ maxLength: 500 }}
              sx={{ flex: 2 }}
            />
            {removeButton(() => set('emails', removePrimaryRow(draft.emails, index)))}
          </Stack>
        ))}
      </Section>

      <Section
        title={t('contact.section.phones')}
        action={
          <AddButton
            label={t('contact.addPhone')}
            onClick={() => set('phones', [...draft.phones, emptyPhone(draft.phones.length === 0)])}
          />
        }
      >
        {draft.phones.map((row, index) => (
          <Stack
            key={index}
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            {primaryButton(row.isPrimary, () => set('phones', markPrimary(draft.phones, index)))}
            <TextField
              size="small"
              type="tel"
              label={t('contacts.phone')}
              helperText={index === 0 ? t('contact.phoneHint') : undefined}
              value={row.phone}
              onChange={(event) => setRow('phones', index, { phone: event.target.value })}
              inputProps={{ maxLength: 40 }}
              sx={{ flex: 2 }}
            />
            <TextField
              size="small"
              label={t('contact.rowLabel')}
              value={row.label}
              onChange={(event) => setRow('phones', index, { label: event.target.value })}
              inputProps={{ maxLength: 60 }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label={t('contact.note')}
              value={row.note}
              onChange={(event) => setRow('phones', index, { note: event.target.value })}
              inputProps={{ maxLength: 500 }}
              sx={{ flex: 2 }}
            />
            {removeButton(() => set('phones', removePrimaryRow(draft.phones, index)))}
          </Stack>
        ))}
      </Section>

      <Section
        title={t('contact.section.addresses')}
        action={
          <AddButton
            label={t('contact.addAddress')}
            onClick={() =>
              set('addresses', [
                ...draft.addresses,
                emptyAddress(draft.addresses.some((row) => row.kind === 'BILLING') ? 'OPERATING' : 'BILLING'),
              ])
            }
          />
        }
      >
        {draft.addresses.map((row, index) => (
          <Box key={index} sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                size="small"
                color={row.kind === 'BILLING' ? 'primary' : 'default'}
                variant={row.kind === 'BILLING' ? 'filled' : 'outlined'}
                label={row.kind === 'BILLING' ? t('contact.billing') : t('contact.operating')}
              />
              {row.kind === 'OPERATING' ? (
                <Button size="small" onClick={() => set('addresses', markBilling(draft.addresses, index))}>
                  {t('contact.makeBilling')}
                </Button>
              ) : null}
              <Box sx={{ flexGrow: 1 }} />
              {removeButton(() => removeRow('addresses', index))}
            </Stack>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('contact.street')}
                  value={row.street}
                  onChange={(event) => setRow('addresses', index, { street: event.target.value })}
                  inputProps={{ maxLength: 300 }}
                />
              </Grid>
              <Grid size={{ xs: 4, md: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('contact.postalCode')}
                  value={row.postalCode}
                  onChange={(event) => setRow('addresses', index, { postalCode: event.target.value })}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid size={{ xs: 8, md: 3 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('contact.city')}
                  value={row.city}
                  onChange={(event) => setRow('addresses', index, { city: event.target.value })}
                  inputProps={{ maxLength: 120 }}
                />
              </Grid>
              <Grid size={{ xs: 4, md: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('contact.countryCode')}
                  placeholder={t('contact.countryHint')}
                  value={row.country}
                  onChange={(event) =>
                    setRow('addresses', index, { country: event.target.value.toUpperCase() })
                  }
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>
              <Grid size={{ xs: 8, md: 12 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('contact.note')}
                  value={row.note}
                  onChange={(event) => setRow('addresses', index, { note: event.target.value })}
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Section>

      <Section
        title={t('contact.section.persons')}
        action={
          <AddButton
            label={t('contact.addPerson')}
            onClick={() => set('persons', [...draft.persons, emptyPerson()])}
          />
        }
      >
        {draft.persons.map((row, index) => (
          <Grid key={index} container spacing={1} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                size="small"
                fullWidth
                label={t('contact.personName')}
                value={row.name}
                onChange={(event) => setRow('persons', index, { name: event.target.value })}
                inputProps={{ maxLength: 200 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                size="small"
                fullWidth
                label={t('contact.role')}
                value={row.role}
                onChange={(event) => setRow('persons', index, { role: event.target.value })}
                inputProps={{ maxLength: 120 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                size="small"
                fullWidth
                type="email"
                label={t('contacts.email')}
                value={row.email}
                onChange={(event) => setRow('persons', index, { email: event.target.value })}
                inputProps={{ maxLength: 320 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                size="small"
                fullWidth
                type="tel"
                label={t('contacts.phone')}
                value={row.phone}
                onChange={(event) => setRow('persons', index, { phone: event.target.value })}
                inputProps={{ maxLength: 40 }}
              />
            </Grid>
            <Grid size={{ xs: 10, md: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                label={t('contact.note')}
                value={row.note}
                onChange={(event) => setRow('persons', index, { note: event.target.value })}
                inputProps={{ maxLength: 500 }}
              />
            </Grid>
            <Grid size={{ xs: 2, md: 0.5 }}>{removeButton(() => removeRow('persons', index))}</Grid>
          </Grid>
        ))}
      </Section>

      <Section
        title={t('contact.section.websites')}
        action={
          <AddButton
            label={t('contact.addWebsite')}
            onClick={() => set('websites', [...draft.websites, emptyWebsite()])}
          />
        }
      >
        {draft.websites.map((row, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              label={t('contact.linkKind')}
              value={row.kind}
              onChange={(event) =>
                setRow('websites', index, { kind: event.target.value as ContactWebsiteKind })
              }
              sx={{ minWidth: 140 }}
            >
              {WEBSITE_KINDS.map((one) => (
                <MenuItem key={one} value={one}>
                  {t(`websiteKind.${one}` as const)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              fullWidth
              label={t('contact.url')}
              value={row.url}
              onChange={(event) => setRow('websites', index, { url: event.target.value })}
              inputProps={{ maxLength: 2000 }}
            />
            {removeButton(() => removeRow('websites', index))}
          </Stack>
        ))}
      </Section>

      <Section title={t('contact.section.description')}>
        <TextField
          fullWidth
          multiline
          minRows={4}
          value={draft.description}
          onChange={(event) => set('description', event.target.value)}
          inputProps={{ maxLength: 20000 }}
        />
      </Section>
    </Stack>
  );
};
